import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { OLAF } from 'HubStyleTokens/colors';
import UIScrollContainer from 'UIComponents/scroll/UIScrollContainer';
import UISearchInput from 'UIComponents/input/UISearchInput';
import UIToggleGroup from 'UIComponents/input/UIToggleGroup';
import FilterToggle from './FilterOption';
import { useDebounceValue } from '../../../hooks/useDebounceValue';
const INPUT_HEIGHT = 32;
const MAX_HEIGHT = 192;
const StyledUIScrollContainer = styled(UIScrollContainer).withConfig({
  displayName: "FilterToggleGroup__StyledUIScrollContainer",
  componentId: "ze4h5u-0"
})(["*::-webkit-scrollbar{display:none;}::-webkit-scrollbar-track{border-radius:8px;}*:hover::-webkit-scrollbar{display:block;width:8px;}*:hover::-webkit-scrollbar-thumb{border-radius:4px;background-color:rgba(235,240,245,1);box-shadow:0 0 1px rgba(255,255,255,0.5);}*::-webkit-scrollbar-thumb:hover{background-color:rgba(223,227,235,1);}"]);
/*
 * Filter list of options if option text contains search word. For nested
 * option groups, only show matching nested options. If no nested options are
 * found, exclude the entire group.
 */

function searchOptions(options, search) {
  if (!search.length) return options;
  const searchRegex = new RegExp(search, 'i');
  return options.reduce((os, option) => {
    if ('options' in option) {
      const nestedOptions = searchOptions(option.options, search);
      if (nestedOptions.length) os.push(Object.assign({}, option, {
        options: nestedOptions
      }));
    } else if (option.text && searchRegex.test(option.text)) {
      os.push(option);
    }

    return os;
  }, []);
}

function FilterToggleGroup({
  ariaLabel,
  disabled,
  inputClassName,
  labelComponent: LabelComponent,
  multi,
  name,
  onChange,
  onSearchBlur,
  onSearchChange,
  options: defaultOptions,
  searchable,
  searchPlaceholder,
  scrollable,
  value,
  virtualized
}) {
  const [options, setOptions] = useState(defaultOptions);
  const [scrollTop, setScrollTop] = useState(0);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounceValue(search);
  const scrollElementRef = useRef(null);
  useEffect(() => {
    if (!debouncedSearch) {
      setOptions(defaultOptions);
      return;
    }

    const filteredOptions = searchOptions(defaultOptions, debouncedSearch);
    setOptions(filteredOptions);
  }, [debouncedSearch, defaultOptions, setOptions]);
  const numOptions = options.length;

  const renderedOptions = () => {
    if (virtualized) {
      const startIndex = Math.floor(scrollTop / INPUT_HEIGHT);
      const endIndex = Math.min(numOptions - 1, Math.floor((scrollTop + MAX_HEIGHT) / INPUT_HEIGHT));
      const visibleOptions = [];

      for (let i = startIndex; i <= endIndex; i++) {
        const option = options[i];
        visibleOptions.push( /*#__PURE__*/_jsx(FilterToggle, {
          LabelComponent: LabelComponent,
          disabled: disabled,
          forceOpen: !!debouncedSearch,
          inputClassName: inputClassName,
          multi: !!multi,
          name: name,
          onChange: onChange,
          option: option,
          style: {
            position: 'absolute',
            top: `${i * INPUT_HEIGHT}px`,
            width: '100%'
          },
          value: value
        }, 'value' in option ? option.value : i));
      }

      return visibleOptions;
    }

    return options.map((option, index) => /*#__PURE__*/_jsx(FilterToggle, {
      LabelComponent: LabelComponent,
      disabled: disabled,
      forceOpen: !!debouncedSearch,
      inputClassName: inputClassName,
      name: name,
      multi: !!multi,
      onChange: onChange,
      option: option,
      value: value
    }, index));
  };

  const toggleGroupStyles = useMemo(() => {
    if (virtualized) {
      return {
        position: 'relative',
        height: numOptions * INPUT_HEIGHT
      };
    }

    return {};
  }, [numOptions, virtualized]);

  const renderedToggleGroup = /*#__PURE__*/_jsx(UIToggleGroup, {
    "aria-label": ariaLabel,
    name: name,
    role: "group",
    style: toggleGroupStyles,
    children: renderedOptions()
  });

  const handleSearchChange = event => {
    setSearch(event.target.value);
    if (onSearchChange) onSearchChange(event);
  };

  const renderedSearchInput = searchable && /*#__PURE__*/_jsx(UISearchInput, {
    className: "m-bottom-2",
    onBlur: onSearchBlur,
    onChange: handleSearchChange,
    placeholder: searchPlaceholder,
    value: search
  });

  useLayoutEffect(() => {
    if (scrollElementRef.current) {
      const scrollElement = scrollElementRef.current;

      const scrollHandler = () => {
        setScrollTop(Math.max(scrollElement.scrollTop, 0));
      };

      scrollElement.addEventListener('scroll', scrollHandler);
      return () => scrollElement.removeEventListener('scroll', scrollHandler);
    }

    return () => {};
  }, []);
  useLayoutEffect(() => {
    if (scrollElementRef.current) {
      scrollElementRef.current.scrollTo(0, 0);
    }
  }, [debouncedSearch]);

  if (scrollable || virtualized) {
    return /*#__PURE__*/_jsxs("div", {
      children: [renderedSearchInput, /*#__PURE__*/_jsx(StyledUIScrollContainer, {
        gradientColor: OLAF,
        maxHeight: MAX_HEIGHT,
        scrollElementRef: scrollElementRef,
        size: 28,
        children: renderedToggleGroup
      })]
    });
  }

  return /*#__PURE__*/_jsxs("div", {
    children: [renderedSearchInput, renderedToggleGroup]
  });
}

export default FilterToggleGroup;